/** Findings → the offers the consent gate shows. Every discovered band is offered; the carrier
 *  decides only the route (a direct CDT0 row, or a §10 Route B carve first). Promotions that
 *  carve the same SKUs two ways are resolved here, never at apply time. */
import { expressibilityAudit } from "./cdtLayout/expressibility";
import {
  promotionRemedyFor,
  restrictionRowsFor,
  type RowFormulation,
} from "./cdtLayout/restrictionRemedy";
import type {
  LengthUnit,
  RestrictionPlacement,
  StudioDraft,
  StudioItem,
  StudioRunGeometry,
} from "./studioTypes";
import type { RestrictionOffer } from "./variantRestrictionUpload";
import { shelfRanksOf } from "./shelfRankSpace";
import { revertPromotion } from "./wizardPromotion";

/**
 * The draft the OPEN offers are read from: the kept rules' carves undone, newest first, so each
 * band is offered as the base shows it whatever was kept before it. Keeping a rule carves its band
 * out of the tree at once (`adoptPromotion`); read on that carved tree, the remaining bands of the
 * same section change under the merchant's hands: once two of fabric care's three bands are kept,
 * the third is no longer "Fabric care Beads" but the section's remainder, offered as "Fabric care"
 * on its shelves, and a stain-remover band can lose its offer altogether (laundry base 235,
 * 2026-10-30). The kept rules stay the snapshots they were kept as; Finalize applies every kept
 * carve together, as it always did.
 */
export const offerBaselineOf = (draft: StudioDraft): StudioDraft =>
  [...(draft.keptPlacementOffers ?? [])]
    .reverse()
    .reduce(
      (current, offer) => (offer.promotion ? revertPromotion(current, offer.bandVariant) : current),
      draft,
    );

/** Content identity so reordering findings does not renumber rules. */
export const placementRuleIdOf = (bandVariant: string, placement: RestrictionPlacement): string =>
  `rule:${bandVariant}@${placement.cells
    .map((cell) => `s${cell.shelf}b${cell.bays.join(".")}`)
    .join("|")}`;

export const buildRestrictionOffers = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
  formulation: RowFormulation = "fence",
  /** The category's unit, for the offers' words only. */
  lengthUnit?: LengthUnit,
): RestrictionOffer[] => {
  const findings = expressibilityAudit(items, runs, draft, { lengthUnit }).findings;

  const offers: RestrictionOffer[] = [];
  const kindOf = new Map<RestrictionOffer, BandKind>();
  const keep = (kind: BandKind, offer: RestrictionOffer) => {
    offers.push(offer);
    kindOf.set(offer, kind);
  };
  for (const finding of findings) {
    if (finding.kind !== "vertical-band" && finding.kind !== "horizontal-band") continue;
    const run = runs.find((candidate) => candidate.run === finding.run);
    if (!run) continue;
    const fixture = { bays: run.bays, shelves: shelfRanksOf(run).length };
    const kind: BandKind = finding.kind;
    // Only a band at CDT0 needs no carve-out; this changes the offer's wording, not its existence.
    const treeHolds = finding.carrier.kind !== "restriction-row";

    if (finding.kind === "vertical-band" && finding.carrier.kind === "restriction-row") {
      const direct = restrictionRowsFor(finding, items, runs, draft, {}, formulation);
      if (!direct) continue;
      keep(kind, {
        bandVariant: direct.variant,
        anchorSkuId: finding.skuIds[0],
        skuIds: finding.skuIds,
        treeHolds,
        rows: direct.rows,
        // Cost comes from the fence rows, not a reservation (siblings × cells).
        cost: {
          cells: direct.cells,
          siblingVariants: direct.siblingVariants,
          evictions: direct.evictions,
          siblingNames: direct.siblingNames,
        },
        placement: direct.placement,
        fence: direct.fence,
        shippedVariants: direct.shippedVariants,
        fixture,
      });
      continue;
    }

    // Below CDT0 a row cannot name the band until Route B carves it out.
    const remedy = promotionRemedyFor(finding, items, runs, draft, { lengthUnit });
    if (!remedy || remedy.rows.length === 0) continue;
    if (!remedy.column.passes) continue;
    const bandVariant = remedy.promotion.bandVariant;
    keep(kind, {
      bandVariant,
      anchorSkuId: finding.skuIds[0],
      skuIds: finding.skuIds,
      treeHolds,
      promotion: remedy.promotion,
      rows: remedy.rows,
      cost: {
        cells: remedy.cells,
        siblingVariants: remedy.siblingVariants,
        evictions: remedy.evictions,
        siblingNames: remedy.siblingNames,
      },
      placement: remedy.placement,
      fence: remedy.fence,
      shippedVariants: remedy.shippedVariants,
      fixture,
    });
  }
  return resolvePromotionConflicts(offers, (offer) => kindOf.get(offer) ?? "vertical-band");
};

type BandKind = "vertical-band" | "horizontal-band";

const retagSkusOf = (offer: RestrictionOffer): ReadonlySet<string> =>
  new Set(offer.promotion ? Object.keys(offer.promotion.retag) : []);

/**
 * Exactly one of two promotions retagging the same SKUs may stand: a row stratum beats a
 * column (a stratum has no carrier but a restriction row; a column is what pogger blocks
 * natively at cdt1), and between two of one kind the earlier in wall order wins. The loser
 * leaves the offer set and is recorded on the winner's `supersedes`.
 */
export const resolvePromotionConflicts = (
  offers: readonly RestrictionOffer[],
  kindOf: (offer: RestrictionOffer) => BandKind,
): RestrictionOffer[] => {
  const rank = (offer: RestrictionOffer) => (kindOf(offer) === "horizontal-band" ? 0 : 1);
  const indexOf = new Map(offers.map((offer, index) => [offer, index]));
  const byPrecedence = [...offers].sort(
    (a, b) => rank(a) - rank(b) || (indexOf.get(a) ?? 0) - (indexOf.get(b) ?? 0),
  );

  const kept: {
    offer: RestrictionOffer;
    skus: ReadonlySet<string>;
    supersedes: NonNullable<RestrictionOffer["supersedes"]>[number][];
  }[] = [];
  for (const offer of byPrecedence) {
    const skus = retagSkusOf(offer);
    const rival =
      skus.size > 0
        ? kept.find((entry) => [...skus].some((sku) => entry.skus.has(sku)))
        : undefined;
    if (!rival) {
      kept.push({ offer, skus, supersedes: [] });
      continue;
    }
    const shared = [...skus].filter((sku) => rival.skus.has(sku)).sort();
    const sameKind = kindOf(offer) === kindOf(rival.offer);
    rival.supersedes.push({
      bandVariant: offer.bandVariant,
      skuIds: shared,
      reason: sameKind
        ? `"${offer.bandVariant}" carves ${shared.length} of the same SKUs as "${rival.offer.bandVariant}" and both are ${kindOf(offer) === "horizontal-band" ? "row strata" : "columns"}; the earlier in wall order stands, this one is refused — a CDT0 can be owned once.`
        : `"${offer.bandVariant}" is a column carve claiming ${shared.length} SKUs that the row stratum "${rival.offer.bandVariant}" already owns. A stratum has no carrier but a restriction, while a column is what pogger blocks natively at cdt1 — a promotion widens CDT0 for strata, it does not spend a vertical slot on a column — so the stratum stands and this carve is refused, never overwritten last-writer-wins.`,
    });
  }

  return kept
    .sort((a, b) => (indexOf.get(a.offer) ?? 0) - (indexOf.get(b.offer) ?? 0))
    .map((entry) =>
      entry.supersedes.length > 0
        ? { ...entry.offer, supersedes: [...(entry.offer.supersedes ?? []), ...entry.supersedes] }
        : entry.offer,
    );
};
